import React, { Suspense, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth, ProtectedRoute, usePermission } from './auth';

// Auth Pages
const LoginPage = React.lazy(() => import('./auth/pages/LoginPage'));
const VerifyOtpPage = React.lazy(() => import('./auth/pages/VerifyOtpPage'));
const SetPasswordPage = React.lazy(() => import('./auth/pages/SetPasswordPage'));
const AdminPanel = React.lazy(() => import('./auth/pages/AdminPanel'));

// Operations Section
const HomePage = React.lazy(() => import('./operations/pages/HomePage'));
const UploadDocx = React.lazy(() => import('./operations/pages/UploadDocx'));
const CandidateDetail = React.lazy(() => import('./operations/pages/CandidateDetail'));
const CourseSelection = React.lazy(() => import('./operations/pages/CourseSelection'));
const CoursePreview = React.lazy(() => import('./operations/pages/CoursePreview'));
const DynamicCertificate = React.lazy(() => import('./operations/pages/certificates/DynamicCertificate'));

// Bookkeeping Section
const BookkeepingDashboard = React.lazy(() => import('./bookkeeping/dashboard/components/BookkeepingDashboard'));
const PaymentReceiptPage = React.lazy(() => import('./bookkeeping/payment-receipt/components/PaymentReceiptEntries'));
const VendorServiceEntry = React.lazy(() => import('./bookkeeping/payment-receipt/components/VendorServiceEntry/VendorServiceEntry'));
const SalesVoucher = React.lazy(() => import('./bookkeeping/payment-receipt/components/SalesVoucher/SalesVoucher'));
const ReceiptEntries = React.lazy(() => import('./bookkeeping/payment-receipt/components/ReceiptEntries/ReceiptEntries'));
const VendorPaymentEntry = React.lazy(() => import('./bookkeeping/payment-receipt/components/VendorPaymentEntry/VendorPaymentEntry'));
const AddExpensePaymentEntry = React.lazy(() => import('./bookkeeping/payment-receipt/components/AddExpensePaymentEntry/AddExpensePaymentEntry'));
const AdjustmentEntry = React.lazy(() => import('./bookkeeping/payment-receipt/components/AdjustmentEntry/AdjustmentEntry'));
const ClientAdjustmentInvoicePreview = React.lazy(() => import('./bookkeeping/payment-receipt/components/AdjustmentEntry/ClientAdjustmentInvoicePreview'));
const VendorAdjustmentInvoicePreview = React.lazy(() => import('./bookkeeping/payment-receipt/components/AdjustmentEntry/VendorAdjustmentInvoicePreview'));
const ReceiptInvoicePreview = React.lazy(() => import('./bookkeeping/payment-receipt/components/ReceiptEntries/ReceiptInvoicePreview'));
const NewStepper = React.lazy(() => import('./bookkeeping/payment-receipt/components/SalesVoucher/NewStepper'));
const InvoicePreview = React.lazy(() => import('./bookkeeping/payment-receipt/components/SalesVoucher/InvoicePreview'));
const InvoiceGeneration = React.lazy(() => import('./bookkeeping/invoice-generation/components/InvoiceGeneration'));
const ProformaGstInvoice = React.lazy(() => import('./bookkeeping/invoice-generation/components/ProformaGstInvoice'));
const ProformaInvoice = React.lazy(() => import('./bookkeeping/invoice-generation/components/ProformaInvoice'));
const DailyMonthlyYearlyLedger = React.lazy(() => import('./bookkeeping/periodic-ledger/components/DailyMonthlyYearlyLedger'));
const SummaryReport = React.lazy(() => import('./bookkeeping/summary-report/components/SummaryReport'));
const RateListEntries = React.lazy(() => import('./bookkeeping/ratelists-entries/components/RateListEntries'));
const LedgerDashboard = React.lazy(() => import('./bookkeeping/ledger-dashboard/components/LedgerDashboard'));

// Database Section
const DatabaseDashboard = React.lazy(() => import('./database/pages/DatabaseDashboard'));
const CandidateRecords = React.lazy(() => import('./database/pages/CandidateRecords'));
const LegacyCertificates = React.lazy(() => import('./database/pages/LegacyCertificates'));
const MasterDataInsert = React.lazy(() => import('./database/pages/MasterDataInsert'));

// Protected Route Component - Using the one from auth module
// Role restrictions are now handled via allowedRoles prop

// Header with user info and logout
function Header() {
  const { user, logout } = useAuth();
  const { isSuperAdmin } = usePermission();
  const navigate = useNavigate();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogoutClick = () => {
    // First click shows confirmation
    setShowLogoutConfirm(true);
  };

  const handleConfirmLogout = async () => {
    setIsLoggingOut(true);
    await logout();
    navigate('/login');
  };

  const handleCancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  // Logout Confirmation Modal
  if (showLogoutConfirm) {
    return (
      <>
        <header style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          padding: '12px 24px',
          background: '#1a1a2e',
          color: 'white'
        }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '18px' }}>Value Added System</h1>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <span style={{ fontSize: '14px', opacity: 0.8 }}>
              {user?.name} ({user?.role})
            </span>
          </div>
        </header>
        
        {/* Logout Confirmation Modal */}
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
          }}>
            {/* Warning Icon */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '16px',
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                backgroundColor: '#FEE2E2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
            </div>

            {/* Title */}
            <h3 style={{
              textAlign: 'center',
              fontSize: '20px',
              fontWeight: '600',
              color: '#1F2937',
              marginBottom: '12px',
              margin: 0,
            }}>
              Confirm Logout
            </h3>

            {/* Warning Message */}
            <p style={{
              textAlign: 'center',
              fontSize: '14px',
              color: '#DC2626',
              fontWeight: '500',
              marginBottom: '8px',
              margin: 0,
            }}>
              ⚠️ This action cannot be reversed
            </p>

            {/* Additional Info */}
            <p style={{
              textAlign: 'center',
              fontSize: '14px',
              color: '#6B7280',
              marginBottom: '24px',
              margin: 0,
            }}>
              You will be logged out and your session will end.
            </p>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              flexDirection: 'column',
            }}>
              {/* Confirm Logout Button - Prominent */}
              <button
                onClick={handleConfirmLogout}
                disabled={isLoggingOut}
                style={{
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'white',
                  backgroundColor: isLoggingOut ? '#9CA3AF' : '#DC2626',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isLoggingOut ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                {isLoggingOut ? 'Logging out...' : 'Yes, Logout'}
              </button>

              {/* Cancel Button */}
              <button
                onClick={handleCancelLogout}
                disabled={isLoggingOut}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  backgroundColor: '#F3F4F6',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  cursor: isLoggingOut ? 'not-allowed' : 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <header style={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      padding: '12px 24px',
      background: '#1a1a2e',
      color: 'white'
    }}>
      <div>
        <h1 style={{ margin: 0, fontSize: '18px' }}>Value Added System</h1>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <span style={{ fontSize: '14px', opacity: 0.8 }}>
          {user?.name} ({user?.role})
        </span>
        {isSuperAdmin() && (
          <button 
            onClick={() => navigate('/admin')}
            style={{
              padding: '8px 16px',
              background: 'transparent',
              border: '1px solid white',
              color: 'white',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            Admin
          </button>
        )}
        <button 
          onClick={handleLogoutClick}
          style={{
            padding: '8px 16px',
            background: '#dc2626',
            border: 'none',
            color: 'white',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px'
          }}
        >
          Logout
        </button>
      </div>
    </header>
  );
}

// Layout component for protected routes
function Layout({ children }) {
  return (
    <>
      <Header />
      {children}
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Suspense fallback={
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-lg">Loading...</div>
          </div>
        }>
          <Routes>
            {/* Public Auth Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/verify-otp" element={<VerifyOtpPage />} />
            <Route path="/set-password" element={<SetPasswordPage />} />

            {/* Protected Routes */}
            <Route path="/" element={
              <ProtectedRoute>
                <Layout>
                  <HomePage />
                </Layout>
              </ProtectedRoute>
            } />

            {/* Operations Section */}
            <Route path="/upload-docx" element={
              <ProtectedRoute>
                <Layout>
                  <UploadDocx />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/candidate-details" element={
              <ProtectedRoute allowedRoles={['Staff', 'SuperAdmin']}>
                <Layout>
                  <CandidateDetail />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/course-selection" element={
              <ProtectedRoute>
                <Layout>
                  <CourseSelection />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/certificate/:certificateType" element={
              <ProtectedRoute allowedRoles={['Staff', 'SuperAdmin']}>
                <Layout>
                  <DynamicCertificate />
                </Layout>
              </ProtectedRoute>
            } />

            {/* Bookkeeping Section */}
            <Route path="/bookkeeping" element={
              <ProtectedRoute>
                <Layout>
                  <BookkeepingDashboard />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/bookkeeping/payment-receipt" element={
              <ProtectedRoute>
                <Layout>
                  <PaymentReceiptPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/bookkeeping/vendor-service-entry" element={
              <ProtectedRoute>
                <Layout>
                  <VendorServiceEntry />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/bookkeeping/sales-voucher" element={
              <ProtectedRoute>
                <Layout>
                  <SalesVoucher />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/bookkeeping/receipt-entries" element={
              <ProtectedRoute>
                <Layout>
                  <ReceiptEntries />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/bookkeeping/receipt-invoice-preview" element={
              <ProtectedRoute>
                <Layout>
                  <ReceiptInvoicePreview />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/bookkeeping/vendor-payment-entry" element={
              <ProtectedRoute>
                <Layout>
                  <VendorPaymentEntry />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/bookkeeping/add-expense-payment-entry" element={
              <ProtectedRoute>
                <Layout>
                  <AddExpensePaymentEntry />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/bookkeeping/adjustment-entry" element={
              <ProtectedRoute allowedRoles={['SuperAdmin']}>
                <Layout>
                  <AdjustmentEntry />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/bookkeeping/client-adjustment-invoice-preview" element={
              <ProtectedRoute>
                <Layout>
                  <ClientAdjustmentInvoicePreview />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/bookkeeping/vendor-adjustment-invoice-preview" element={
              <ProtectedRoute>
                <Layout>
                  <VendorAdjustmentInvoicePreview />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/bookkeeping/new-invoice-stepper" element={
              <ProtectedRoute>
                <Layout>
                  <NewStepper />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/bookkeeping/invoice-preview" element={
              <ProtectedRoute>
                <Layout>
                  <InvoicePreview />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/bookkeeping/invoice-generation" element={
              <ProtectedRoute allowedRoles={['Staff', 'SuperAdmin']}>
                <Layout>
                  <InvoiceGeneration />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/bookkeeping/tax-invoice" element={
              <ProtectedRoute allowedRoles={['Staff', 'SuperAdmin']}>
                <Layout>
                  <ProformaGstInvoice />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/bookkeeping/proforma-invoice" element={
              <ProtectedRoute allowedRoles={['Staff', 'SuperAdmin']}>
                <Layout>
                  <ProformaInvoice />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/bookkeeping/ledger" element={
              <ProtectedRoute>
                <Layout>
                  <DailyMonthlyYearlyLedger />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/bookkeeping/summary-report" element={
              <ProtectedRoute allowedRoles={['SuperAdmin']}>
                <Layout>
                  <SummaryReport />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/bookkeeping/ratelist-entries" element={
              <ProtectedRoute>
                <Layout>
                  <RateListEntries />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/bookkeeping/ledger-dashboard" element={
              <ProtectedRoute>
                <Layout>
                  <LedgerDashboard />
                </Layout>
              </ProtectedRoute>
            } />

            {/* Database Section */}
            <Route path="/database" element={
              <ProtectedRoute>
                <Layout>
                  <DatabaseDashboard />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/candidate-records" element={
              <ProtectedRoute allowedRoles={['SuperAdmin']}>
                <Layout>
                  <CandidateRecords />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/legacy-certificates" element={
              <ProtectedRoute>
                <Layout>
                  <LegacyCertificates />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/master-data-insert" element={
              <ProtectedRoute allowedRoles={['SuperAdmin']}>
                <Layout>
                  <MasterDataInsert />
                </Layout>
              </ProtectedRoute>
            } />

            {/* Admin Routes - SuperAdmin Only */}
            <Route path="/admin" element={
              <ProtectedRoute allowedRoles={['SuperAdmin']}>
                <Layout>
                  <AdminPanel />
                </Layout>
              </ProtectedRoute>
            } />

            {/* Catch all - redirect to home or login */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </Router>
    </AuthProvider>
  );
}

export default App;
